import { Box, CardMedia, Tooltip, Typography } from '@mui/material';
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Zoomist from 'zoomist';
import 'zoomist/css';
import { HeapDetection, normalizeModelClass } from './types';
import { getColorForClass } from './utils';
import { HeapMaskOverlay } from './heapMasks';
import { HeapSegmentationOverlay } from './HeapSegmentationOverlay';

export interface BoundingBox {
  rel_x1: number;
  rel_y1: number;
  rel_x2: number;
  rel_y2: number;
  color: string;
  className: string;
  detectionIndex: number;
  borderStyle: 'dashed' | 'solid';
}

interface HeapImageWithBoxesProps {
  imageSrc: string;
  detections: HeapDetection[];
  invalidDetectionIndexes?: number[];
  onDetectionClick?: (detectionIndex: number) => void;
  imageWidth?: number;
  imageHeight?: number;
  segmentationOverlays?: readonly HeapMaskOverlay[];
}

export const HeapImageWithBoxes = memo(function ({
  imageSrc,
  detections,
  invalidDetectionIndexes,
  onDetectionClick,
  imageWidth = 2464,
  imageHeight = 2064,
  segmentationOverlays,
}: HeapImageWithBoxesProps): ReactElement {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomistContainerRef = useRef<HTMLDivElement>(null);
  const zoomistRef = useRef<Zoomist | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [sourceDimensions, setSourceDimensions] = useState({ width: imageWidth, height: imageHeight });
  const [renderedDimensions, setRenderedDimensions] = useState({ width: 0, height: 0 });
  const [zoomScale, setZoomScale] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);

  const invalidDetectionIndexSet = useMemo(() => new Set(invalidDetectionIndexes ?? []), [invalidDetectionIndexes]);
  const isInvalidationEnabled = invalidDetectionIndexes !== undefined && onDetectionClick !== undefined;

  const boundingBoxes: BoundingBox[] = useMemo(() => {
    const width = sourceDimensions.width || imageWidth;
    const height = sourceDimensions.height || imageHeight;

    return detections.flatMap((detection, detectionIndex) => {
      if (invalidDetectionIndexSet.has(detectionIndex)) {
        return [];
      }

      const [x_min, y_min, x_max, y_max] = detection.bbox;

      const rel_x1 = Math.max(0, Math.min(1, x_min / width));
      const rel_y1 = Math.max(0, Math.min(1, y_min / height));
      const rel_x2 = Math.max(rel_x1, Math.min(1, x_max / width));
      const rel_y2 = Math.max(rel_y1, Math.min(1, y_max / height));

      const className = detection.classes[0] || 'unknown';
      const color = getColorForClass(className);
      const modelClass = normalizeModelClass(detection.sources?.[0]);

      return {
        rel_x1,
        rel_y1,
        rel_x2,
        rel_y2,
        color,
        className,
        borderStyle: modelClass === 'stable' ? 'solid' : 'dashed',
        detectionIndex,
      };
    });
  }, [detections, imageHeight, imageWidth, invalidDetectionIndexSet, sourceDimensions.height, sourceDimensions.width]);

  const updateRenderedDimensions = useCallback(() => {
    if (imageRef.current) {
      setRenderedDimensions({
        width: imageRef.current.clientWidth,
        height: imageRef.current.clientHeight,
      });
    }
  }, []);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const naturalWidth = img.naturalWidth || imageWidth;
      const naturalHeight = img.naturalHeight || imageHeight;

      setSourceDimensions({ width: naturalWidth, height: naturalHeight });
      setImageLoaded(true);
      updateRenderedDimensions();
    },
    [imageHeight, imageWidth, updateRenderedDimensions]
  );

  const handleImageError = useCallback(() => {
    setImageLoaded(false);
  }, []);

  useEffect(() => {
    if (!imageLoaded || !zoomistContainerRef.current) return undefined;

    const zoomist = new Zoomist(zoomistContainerRef.current, {
      maxScale: 10,
      minScale: 1,
      bounds: true,
      zoomer: true,
      slider: false,
      on: {
        zoom: (instance) => {
          setZoomScale(instance.transform.scale);
        },
        ready: (instance) => {
          setZoomScale(instance.transform.scale);
        },
        reset: (instance) => {
          setZoomScale(instance.transform.scale);
        },
      },
    });

    zoomistRef.current = zoomist;

    return () => {
      if (zoomistRef.current) {
        zoomistRef.current.destroy();
        zoomistRef.current = null;
      }
    };
  }, [imageLoaded, imageSrc]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      updateRenderedDimensions();
    });
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [updateRenderedDimensions]);

  useEffect(() => {
    setImageLoaded(false);
    setZoomScale(1);
  }, [imageSrc]);

  return (
    <Box display='flex' flexDirection='column' alignItems='center' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
          minHeight: 0,
          width: '100%',
          height: '100%',
        }}
      >
        {/* Zoomist container structure */}
        <div ref={zoomistContainerRef} className='zoomist-container' style={{ width: '100%', height: '100%' }}>
          <div className='zoomist-wrapper' style={{ width: '100%', height: '100%' }}>
            <div
              className='zoomist-image'
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <CardMedia
                component='img'
                ref={imageRef}
                src={imageSrc}
                sx={{
                  display: 'block',
                  maxHeight: '100%',
                  maxWidth: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '10px',
                }}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              {imageLoaded && renderedDimensions.width > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    width: renderedDimensions.width,
                    height: renderedDimensions.height,
                    pointerEvents: 'none',
                  }}
                >
                  {segmentationOverlays?.map((overlay) => (
                    <HeapSegmentationOverlay
                      key={overlay.id}
                      overlay={overlay}
                      renderedWidth={renderedDimensions.width}
                      renderedHeight={renderedDimensions.height}
                    />
                  ))}
                  {boundingBoxes.map((bb) => (
                    <BoundingBoxOverlay
                      key={`bb-${bb.detectionIndex}`}
                      boundingBox={bb}
                      className={t(`heap.classes.${bb.className}`, { defaultValue: bb.className })}
                      zoomScale={zoomScale}
                      onClick={
                        isInvalidationEnabled && onDetectionClick
                          ? () => {
                              onDetectionClick(bb.detectionIndex);
                            }
                          : undefined
                      }
                    />
                  ))}
                </Box>
              )}
            </div>
          </div>
        </div>
      </Box>
    </Box>
  );
});

interface BoundingBoxProps {
  boundingBox: BoundingBox;
  className: string;
  zoomScale: number;
  onClick?: () => void;
}

const BoundingBoxOverlay = ({ boundingBox, className, zoomScale, onClick }: BoundingBoxProps): ReactElement => {
  const left = boundingBox.rel_x1 * 100;
  const top = boundingBox.rel_y1 * 100;
  const width = (boundingBox.rel_x2 - boundingBox.rel_x1) * 100;
  const height = (boundingBox.rel_y2 - boundingBox.rel_y1) * 100;
  const { t } = useTranslation();

  const baseBorderWidth = 2;
  const adjustedBorderWidth = Math.max(1, baseBorderWidth / zoomScale);

  return (
    <Tooltip
      title={
        <Box padding={0.5}>
          <Typography variant='body2'>{className}</Typography>
          <Typography variant='caption'>{t('heap.clickToInvalidate')}</Typography>
        </Box>
      }
    >
      <Box
        onClick={(event) => {
          if (!onClick) return;
          event.preventDefault();
          event.stopPropagation();
          onClick();
        }}
        sx={{
          position: 'absolute',
          top: `${top}%`,
          left: `${left}%`,
          width: `${width}%`,
          height: `${height}%`,
          border: `${adjustedBorderWidth}px ${boundingBox.borderStyle} ${boundingBox.color}`,
          borderRadius: `${3 / zoomScale}px`,
          pointerEvents: onClick ? 'auto' : 'none',
          cursor: onClick ? 'pointer' : 'default',
        }}
      />
    </Tooltip>
  );
};
